import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Mail, Loader2 } from "lucide-react";
import { api, setToken, getToken, formatApiErrorDetail } from "../lib/adminApi";
import BrandLogo from "../components/BrandLogo";
import { Waves } from "../components/Decor";

export default function AdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    if (getToken()) return <Navigate to="/admin/leads" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setToken(data.token);
            navigate("/admin/leads", { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-5 relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 pointer-events-none">
                <Waves className="w-full h-14 rotate-180" color="#1B6CA8" />
            </div>

            <div className="w-full max-w-md relative">
                <div className="flex flex-col items-center mb-10">
                    <BrandLogo variant="primary" className="w-40 mb-6" />
                    <p className="text-[11px] uppercase tracking-[0.32em] text-coral font-bold">
                        Espace admin
                    </p>
                    <h1 className="font-serif text-4xl text-ocean mt-2">
                        Connexion
                    </h1>
                </div>

                <form
                    onSubmit={submit}
                    className="bg-white border border-ocean/10 rounded-2xl p-8 shadow-[0_20px_60px_rgba(27,108,168,0.08)]"
                    data-testid="admin-login-form"
                >
                    <label className="block mb-5">
                        <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-ocean mb-2 flex items-center gap-2">
                            <Mail size={13} /> Email
                        </span>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 bg-cream border border-ocean/20 rounded-lg text-ocean focus:border-coral focus:outline-none transition-colors"
                            placeholder="admin@exotiktrips.com"
                            data-testid="admin-login-email"
                        />
                    </label>
                    <label className="block mb-6">
                        <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-ocean mb-2 flex items-center gap-2">
                            <Lock size={13} /> Mot de passe
                        </span>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 bg-cream border border-ocean/20 rounded-lg text-ocean focus:border-coral focus:outline-none transition-colors"
                            placeholder="••••••••"
                            data-testid="admin-login-password"
                        />
                    </label>
                    {error && (
                        <div
                            className="mb-5 p-3 bg-coral/10 border border-coral/30 rounded-lg text-coral text-sm"
                            data-testid="admin-login-error"
                        >
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-coral w-full justify-center disabled:opacity-60"
                        data-testid="admin-login-submit"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                Connexion…
                            </>
                        ) : (
                            <>Se connecter →</>
                        )}
                    </button>
                </form>

                <p className="text-center text-xs text-ink/50 mt-6">
                    Accès réservé — Exotik Trips © {new Date().getFullYear()}
                </p>
            </div>
        </div>
    );
}
